/**
 * GDSCardUI.js
 * Manages direct DOM manipulation and UI updates for product cards.
 * FIXED: Image loading timeout and getCurrentCardData issues
 */
import { getSizedShopifyUrl, formatMoney, CARD_IMAGE_DESIRED_WIDTH, CARD_IMAGE_DESIRED_HEIGHT, PROPAGATING_FIELDS } from './GDSModules.js';

export class GDSCardUI {
    constructor(manager) {
        this.manager = manager;
    }

    get contentContainer() { return this.manager.contentContainer; }

    async setupImageLoadingForAllCards() {
        const allCardImages = Array.from(this.contentContainer.querySelectorAll('.product-card .image-container img'));

        if (allCardImages.length === 0) {
            return;
        }

        const imageLoadPromises = allCardImages.map((imgEl, index) => {
            return new Promise((resolve) => {

                // Check if already loaded AND visible (naturalHeight > 0)
                if (imgEl.complete && imgEl.naturalHeight > 0) {
                    resolve({ status: 'loaded', element: imgEl });
                    return;
                }

                // For development environments, use a longer timeout
                const isDevelopment = window.location.hostname === 'localhost' ||
                                      window.location.hostname === '127.0.0.1';
                const timeoutDuration = isDevelopment ? 15000 : 10000; // 15s for dev, 10s for prod

                // Set a timeout to ensure the promise resolves
                const timeout = setTimeout(() => {
                    // In development, just log a debug message instead of warning
                    if (isDevelopment) {
                        // console.log(`[CardUI] Image ${index} timeout after ${timeoutDuration/1000}s (development):`, imgEl.src);
                    } else {
                        console.warn(`[CardUI] Image ${index} timed out after ${timeoutDuration/1000}s:`, imgEl.src);
                    }
                    resolve({ status: 'timeout', element: imgEl });
                }, timeoutDuration);

                // Set up load handler
                const handleLoad = () => {
                    clearTimeout(timeout);
                    imgEl.onload = null;
                    imgEl.onerror = null;
                    resolve({ status: 'loaded', element: imgEl });
                };

                // Set up error handler
                const handleError = () => {
                    console.error(`[CardUI] Image ${index} error:`, imgEl.src);
                    clearTimeout(timeout);
                    imgEl.onload = null;
                    imgEl.onerror = null;
                    resolve({ status: 'error', element: imgEl });
                };

                imgEl.onload = handleLoad;
                imgEl.onerror = handleError;

                // Only force reload if the image hasn't started loading
                if (imgEl.src && !imgEl.complete) {
                    // Create a new Image object to test if the URL is accessible
                    const testImg = new Image();
                    testImg.onload = () => {
                        // URL is valid, trigger reload on the actual element
                        const src = imgEl.src;
                        imgEl.src = '';
                        void imgEl.offsetHeight; // Force reflow
                        imgEl.src = src;
                    };
                    testImg.onerror = handleError;
                    testImg.src = imgEl.src;
                }
            });
        });

        try {
            const settledResults = await Promise.allSettled(imageLoadPromises);

            let loadedCount = 0;
            let errorCount = 0;
            let timeoutCount = 0;

            settledResults.forEach((result, index) => {
                if (result.status === 'fulfilled' && result.value?.element) {
                    const imgEl = result.value.element;
                    const imageContainer = imgEl.closest('.image-container');

                    if (result.value.status === 'loaded') {
                        loadedCount++;
                    } else if (result.value.status === 'error') {
                        errorCount++;
                        if (imageContainer) {
                            imageContainer.classList.remove('image-loading');
                            imageContainer.classList.add('image-error');
                        }
                    } else if (result.value.status === 'timeout') {
                        timeoutCount++;
                        // Don't treat timeout as error in development
                        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
                            if (imageContainer) {
                                imageContainer.classList.remove('image-loading');
                                imageContainer.classList.add('image-timeout');
                            }
                        }
                    }

                    if (imageContainer && result.value.status === 'loaded') {
                        imageContainer.classList.remove('image-loading');
                        imageContainer.classList.remove('image-error');
                        imageContainer.classList.remove('image-timeout');
                    }
                }
            });

        } catch (error) {
            console.error('[CardUI] Unexpected error in setupImageLoadingForAllCards:', error);
        }
    }

    // Helper method to get card data from the correct location
    _getCardData(card) {
        const cardTitle = card?.dataset.cardTitle;
        if (!cardTitle) return null;

        // Direct access to state context
        return this.manager.state.context.main_cards?.[cardTitle] ||
               this.manager.state.context.additional_cards?.[cardTitle] || null;
    }

    setupFieldVisibility() {
        // Get all cards in the builder
        const allCards = this.contentContainer.querySelectorAll('.product-card');

        // First pass - determine which cards should show which fields
        // We need to identify the first card that contains each propagating field
        const cardsWithFields = new Map(); // Map of field name to card element

        allCards.forEach(card => {
            // Get card data using the helper method
            const cardData = this._getCardData(card);

            if (!cardData || !cardData.dropdowns) return;

            // Check each field defined in the card
            cardData.dropdowns.forEach(dropdown => {
                const fieldName = Object.keys(dropdown)[0];

                // If this field should propagate and we haven't seen it yet, mark this card
                if (PROPAGATING_FIELDS.includes(fieldName) && !cardsWithFields.has(fieldName)) {
                    cardsWithFields.set(fieldName, card);
                }
            });
        });

        // Second pass - apply visibility rules to all cards
        allCards.forEach(card => {
            // Skip cards that don't have a modal
            const modalId = card.getAttribute('data-modal-id');
            if (!modalId) return;

            const modal = document.getElementById(modalId);
            if (!modal) return;

            // Get card data using the helper method
            const cardData = this._getCardData(card);

            if (!cardData || !cardData.dropdowns) return;

            // Process each field in this card
            cardData.dropdowns.forEach(dropdown => {
                const fieldName = Object.keys(dropdown)[0];

                // Only apply visibility rules to propagating fields
                if (PROPAGATING_FIELDS.includes(fieldName)) {
                    // Determine if this field should be visible on this card
                    const shouldBeVisible = cardsWithFields.get(fieldName) === card;

                    // Update the field's visibility in the modal
                    this.updateFieldVisibility(modal, fieldName, shouldBeVisible);
                }
            });
        });
    }

    runFinalStylingPass() {
        this.contentContainer.querySelectorAll('.product-card').forEach(card => {
            const isAvailable = card.dataset.variantAvailable === 'true';
            this.updateCardAvailabilityStyling(card, isAvailable);
        });
    }

    /**
     * Toggles the selected state of a card, but only if the product is available.
     * @param {HTMLElement} card - The card element to toggle.
     */
    async toggleCardSelection(card) {
        if (card.dataset.variantAvailable === 'false') {
            // console.warn('[CardUI] Attempted to select an unavailable product. Action prevented.', { card });
            return; // Exit the function immediately, preventing selection.
        }

        const isSelected = card.hasAttribute('data-selected');
        this.setCardSelectedState(card, !isSelected);
        this.updateSubtotal();

        if (this.currentStep === 3) {
            clearTimeout(this._selectionUpdateTimeout);
            this._selectionUpdateTimeout = setTimeout(async () => {
                // For card selection changes within step 3, use replaceState
                // and indicate it's a selection-only change.
                await this.updateURLWithNewSnapshotId(true, true);
            }, 500);
        }
    }

    setCardSelectedState(card, selected) {
        // Get ALL checkmark SVGs in the card (both mobile and desktop)
        const checkmarks = card.querySelectorAll('.checkmark-svg');

        if (checkmarks.length > 0) {
            if (selected) {
                // Select card
                card.setAttribute('data-selected', 'true');
                card.style.border = '3px solid #00B588';

                // Show all checkmarks
                checkmarks.forEach(checkmark => {
                    checkmark.style.display = 'block';
                });

                // Update all checkmark containers
                const checkmarkContainers = card.querySelectorAll('.absolute.top-2.right-2 .h-6.w-6');
                checkmarkContainers.forEach(container => {
                    container.className = 'h-6 w-6 bg-[#00B588] rounded-full flex items-center justify-center shadow-md';
                });
            } else {
                // Unselect card
                card.removeAttribute('data-selected');
                card.style.border = '1px solid #dcdcdc';

                // Hide all checkmarks
                checkmarks.forEach(checkmark => {
                    checkmark.style.display = 'none';
                });

                // Reset all checkmark containers
                const checkmarkContainers = card.querySelectorAll('.absolute.top-2.right-2 .h-6.w-6');
                checkmarkContainers.forEach(container => {
                    container.className = 'h-6 w-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-gray';
                });

                // Hide options row if visible
                const cardTitle = card.getAttribute('data-card-title');
                const optionsRow = this.contentContainer.querySelector(`.card-options-row[data-for-card="${cardTitle}"]`);
                if (optionsRow && !optionsRow.classList.contains('hidden')) {
                    optionsRow.classList.add('hidden');

                    // Reset button text
                    const button = card.querySelector('.toggle-options-btn');
                    if (button) {
                        button.textContent = 'Configure Options';
                    }
                }
            }
        }
    }

    updateSubtotal() {
        const subtotalMobile = document.getElementById('subtotal-amount');
        const subtotalDesktop = document.getElementById('subtotal-amount-desktop');

        if (!subtotalMobile && !subtotalDesktop) return;

        let total = 0;
        const selectedCards = this.contentContainer.querySelectorAll('.product-card[data-selected="true"]');

        selectedCards.forEach(card => {
            const priceStr = card.getAttribute('data-product-price');

            if (priceStr) {
                const price = parseFloat(priceStr);
                if (!isNaN(price)) {
                    total += price;
                } else {
                    console.warn(`[CardUI] Invalid price value: ${priceStr}`);
                }
            } else {
                console.warn('[CardUI] Card is missing data-product-price attribute');
            }
        });

        // Format total as currency
        const formattedTotal = '$' + total.toFixed(2);

        // Update both mobile and desktop subtotals
        if (subtotalMobile) subtotalMobile.textContent = formattedTotal;
        if (subtotalDesktop) subtotalDesktop.textContent = formattedTotal;
    }

    updateCardWithProductData(card, product, selections) {
        if (!card || !product) {
            console.error("[updateCardWPD CRITICAL] Card or product data is missing.");
            return;
        }

        const cardIdentifier = card.getAttribute('data-product-handle') || card.getAttribute('data-card-title') || 'UpdateWPDTarget';
        const variant = product.variants && product.variants[0] ? product.variants[0] : null;
        const variantAvailable = variant ? variant.available : false;
        const formattedPrice = formatMoney(product.price || 0);
        const imageUrl = product.featured_image || '';
        const priceForAttr = ( (product.price || 0) / 100 ).toFixed(2);

        card.setAttribute('data-product-id', product.id);
        card.setAttribute('data-variant-id', variant ? variant.id : '');
        card.setAttribute('data-product-handle', product.handle);
        card.setAttribute('data-product-price', priceForAttr);
        card.setAttribute('data-formatted-price', formattedPrice);
        card.setAttribute('data-variant-available', variantAvailable ? 'true' : 'false');
        card.setAttribute('data-selections', JSON.stringify(selections));

        // Update Visual Elements using specific selectors
        this.updateCardVisuals(card, product, variant, formattedPrice, imageUrl);

        // Update Availability Styling
        this.updateCardAvailabilityStyling(card, variantAvailable);

        // Make sure to update the displayed title in the card
        const titleElements = card.querySelectorAll('.product-title');
        titleElements.forEach(el => {
            el.textContent = product.title;
        });

        this.updateSubtotal();
    }

    updateCardVisuals(card, product, variant, formattedPrice, imageUrl) {
        const cardIdentifierForLog = card.getAttribute('data-product-handle') || card.getAttribute('data-card-title') || 'Unknown Card';

        const mobileWrapper = card.querySelector(':scope > .block.md\\:hidden');
        const desktopWrapper = card.querySelector(':scope > .hidden.md\\:block');

        const mobileTitle = mobileWrapper?.querySelector('h3.product-title');
        const desktopTitle = desktopWrapper?.querySelector('h3.product-title');

        const mobileSku = mobileWrapper?.querySelector('.text-xs.text-gray-500');
        const desktopSku = desktopWrapper?.querySelector('.text-xs.text-gray-500');

        const mobilePrice = mobileWrapper?.querySelector('.text-sm.font-semibold.mt-1');
        const desktopPrice = desktopWrapper?.querySelector('.text-sm.font-semibold.mt-1');

        const mobileImageContainer = mobileWrapper?.querySelector('.image-container');
        const desktopImageContainer = desktopWrapper?.querySelector('.image-container');
        const mobileImage = mobileImageContainer?.querySelector('img');
        const desktopImage = desktopImageContainer?.querySelector('img');

        const titleElements = [mobileTitle, desktopTitle].filter(el => el);
        const skuElements = [mobileSku, desktopSku].filter(el => el);
        const priceElements = [mobilePrice, desktopPrice].filter(el => el);
        const imageElements = [mobileImage, desktopImage].filter(el => el);
        const imageContainers = [mobileImageContainer, desktopImageContainer].filter(el => el);

        if (titleElements.length > 0) {
            titleElements.forEach(titleElement => {
                titleElement.textContent = product.title;
            });
        }

        if (skuElements.length > 0) {
            const skuValue = variant ? (variant.sku || product.handle || '') : (product.handle || '');
            skuElements.forEach(skuElement => {
                skuElement.textContent = skuValue;
            });
        }

        if (priceElements.length > 0) {
            const shouldShowPrice = product.price > 0;
            priceElements.forEach(priceElement => {
                if (priceElement) {
                    priceElement.textContent = formattedPrice;
                    priceElement.style.display = shouldShowPrice ? '' : 'none';
                }
            });
        } else {
            this.createPriceElementsIfMissing(card, mobileWrapper, desktopWrapper, formattedPrice, product.price > 0);
        }

        if (imageElements.length > 0 && imageUrl) {
            const sizedImageUrl = getSizedShopifyUrl(imageUrl, CARD_IMAGE_DESIRED_WIDTH, CARD_IMAGE_DESIRED_HEIGHT);

            imageElements.forEach((imgElement, index) => {
                const container = imageContainers[index];

                if (container) {
                    container.classList.add('image-loading');
                }

                imgElement.onload = () => {
                    if (container) {
                        container.classList.remove('image-loading');
                        container.classList.remove('image-error');
                    }
                };
                imgElement.onerror = () => {
                    if (container) {
                        container.classList.remove('image-loading');
                        container.classList.add('image-error');
                    }
                    imgElement.alt = 'Error loading image';
                };
                imgElement.src = sizedImageUrl;
                imgElement.alt = product.title || 'Product Image';

                if (!imgElement.hasAttribute('width')) imgElement.setAttribute('width', CARD_IMAGE_DESIRED_WIDTH.toString());
                if (!imgElement.hasAttribute('height')) imgElement.setAttribute('height', CARD_IMAGE_DESIRED_HEIGHT.toString());
            });
        }

        const previewButtons = card.querySelectorAll('.preview-eye-btn');
        previewButtons.forEach(button => {
            button.setAttribute('data-product-handle', product.handle);
            button.setAttribute('data-product-id', product.id);
        });

        this.updateSubtotal();
    }

    updateCardAvailabilityStyling(card, variantAvailable) {
        if (!card) {
            console.error('[Styling CRITICAL] updateCardAvailabilityStyling called with no card!');
            return;
        }

        const cardIdentifier = card.getAttribute('data-product-handle') || card.getAttribute('data-card-title') || 'Unknown Card';
        const cardElementIdForLog = card.id || 'CardHasNoID';

        const mobileWrapper = card.querySelector(':scope > .block.md\\:hidden');
        const desktopWrapper = card.querySelector(':scope > .hidden.md\\:block');
        const mobileImageContainer = mobileWrapper?.querySelector('.aspect-square.bg-gray-50.relative');

        if (!variantAvailable) {
            card.classList.add('unavailable-product', 'cursor-not-allowed');

            if (desktopWrapper) {
                let desktopOverlay = desktopWrapper.querySelector('.coming-soon-overlay');
                if (!desktopOverlay) {
                    desktopWrapper.insertAdjacentHTML('afterbegin', this._getComingSoonDesktopOverlayHTML());
                    desktopOverlay = desktopWrapper.querySelector('.coming-soon-overlay');
                }
                if (desktopOverlay) desktopOverlay.style.display = 'flex';
            }

            if (mobileImageContainer) {
                let mobileBadgeContainer = mobileImageContainer.querySelector('.absolute.inset-0.z-16');
                if (!mobileBadgeContainer) {
                    mobileImageContainer.insertAdjacentHTML('beforeend', this._getComingSoonMobileBadgeContainerHTML());
                    mobileBadgeContainer = mobileImageContainer.querySelector('.absolute.inset-0.z-16');
                }
                if (mobileBadgeContainer) mobileBadgeContainer.style.display = 'flex';
            }

            if (mobileWrapper) {
                let mobileImgOverlay = mobileWrapper.querySelector('.mobile-image-overlay');
                if(!mobileImgOverlay){
                    mobileWrapper.insertAdjacentHTML('beforeend', '<div class="absolute inset-0 mobile-image-overlay"></div>');
                }
            }

            if (card.hasAttribute('data-selected')) {
                this.setCardSelectedState(card, false);
            }

        } else {
            card.classList.remove('unavailable-product', 'cursor-not-allowed');

            const desktopOverlaysToRemove = card.querySelectorAll(':scope > .coming-soon-overlay.md\\:flex, :scope > .hidden.md\\:block > .coming-soon-overlay');
            if (desktopOverlaysToRemove.length > 0) {
                desktopOverlaysToRemove.forEach(overlay => {
                    overlay.remove();
                });
            }

            if (mobileImageContainer) {
                const mobileBadgeContainerToRemove = mobileImageContainer.querySelector('.absolute.inset-0.z-16');
                if (mobileBadgeContainerToRemove) {
                    mobileBadgeContainerToRemove.remove();
                }
            }

            if (mobileWrapper) {
                const mobileImgOverlayToRemove = mobileWrapper.querySelector('.mobile-image-overlay');
                if(mobileImgOverlayToRemove){
                    mobileImgOverlayToRemove.remove();
                }
            }
        }
    }

    // Add missing helper methods
    _getComingSoonDesktopOverlayHTML() {
        // This matches the structure in createProductCard
        return `
            <div class="absolute inset-0 z-15 hidden md:flex items-center justify-center rounded-3xl pointer-events-none coming-soon-overlay">
                <div class="coming-soon-badge text-white px-4 py-2 rounded-full transform -rotate-12 font-bold text-lg">
                    Coming Soon
                </div>
            </div>`;
    }

    _getComingSoonMobileBadgeContainerHTML() {
        // This matches the structure in createProductCard for the mobile badge container
        return `
            <div class="absolute inset-0 flex items-center justify-center z-16 mobile-coming-soon-container">
                <div class="mobile-coming-soon-badge text-white px-2 py-1 rounded-full transform -rotate-30 font-bold text-mobile-xxs">
                    Coming Soon
                </div>
            </div>`;
    }

    createPriceElementsIfMissing(card, mobileWrapper, desktopWrapper, formattedPrice, shouldShow) {
        // For mobile view
        if (mobileWrapper) {
            const mobileDetails = mobileWrapper.querySelector('.w-3\\/5.p-3.flex.flex-col.justify-center');
            if (mobileDetails && !mobileDetails.querySelector('.text-sm.font-semibold.mt-1')) {
                const mobilePriceElem = document.createElement('p');
                mobilePriceElem.className = 'text-sm font-semibold mt-1';
                mobilePriceElem.textContent = formattedPrice;
                mobilePriceElem.style.display = shouldShow ? '' : 'none';
                mobileDetails.appendChild(mobilePriceElem);
            }
        }

        // For desktop view
        if (desktopWrapper) {
            const desktopContent = desktopWrapper.querySelector('.p-3');
            if (desktopContent && !desktopContent.querySelector('.text-sm.font-semibold.mt-1')) {
                const desktopPriceElem = document.createElement('p');
                desktopPriceElem.className = 'text-sm font-semibold mt-1';
                desktopPriceElem.textContent = formattedPrice;
                desktopPriceElem.style.display = shouldShow ? '' : 'none';
                desktopContent.appendChild(desktopPriceElem);
            }
        }
    }

    /**
     * Handles adding/removing secondary product cards associated with a main card.
     * @param {HTMLElement} mainCard - The primary card of the group.
     * @param {string[]} additionalSKUs - An array of SKUs for the secondary products.
     * @param {object} selections - The option selections to maintain consistency.
     */
    async handleAdditionalProducts(mainCard, additionalSKUs, selections) {
        const container = mainCard.closest('#main-cards-container, #additional-cards-container');
        if (!container) {
            console.warn('[CardUI] Could not find container for card in handleAdditionalProducts.');
            return;
        }

        const cardTitle = mainCard.getAttribute('data-card-title');
        if (!cardTitle) {
            console.warn('[CardUI] Main card is missing data-card-title in handleAdditionalProducts.');
            return;
        }

        // 1. UI Task: Remove existing secondary cards. This is a UI job, so it stays here.
        this.removeSecondaryCards(mainCard);

        // 2. Orchestration Task: Delegate the creation of new cards to the manager.
        for (let i = 0; i < additionalSKUs.length; i++) {
            const sku = additionalSKUs[i];
            try {
                // CORRECTED: Call the manager to do the heavy lifting.
                await this.manager.createSecondaryCardWithIsolation(sku, mainCard, container, i + 1, cardTitle);
            } catch (error) {
                console.error(`[CardUI] Error while manager was creating secondary card for SKU ${sku}:`, error);
            }
        }

        // 3. UI Task: After all changes, ensure field visibility is correct.
        this.setupFieldVisibility();
    }

    removeSecondaryCards(mainCard) {
        const container = mainCard.closest('#main-cards-container, #additional-cards-container');
        const cardTitle = mainCard.getAttribute('data-card-title');
        if (!container || !cardTitle) return;

        const secondaryCards = Array.from(container.querySelectorAll(`.product-card[data-card-title="${cardTitle}"]`))
                                         .filter(c => c !== mainCard);

        if (secondaryCards.length > 0) {
            secondaryCards.forEach(sc => {
                // *** FIX: Remove the associated modal before removing the card ***
                const modalId = sc.dataset.modalId;
                if (modalId) {
                    const modalElement = document.getElementById(modalId);
                    if (modalElement) {
                        modalElement.remove();
                    }
                }
                sc.remove();
            });
            this.setupFieldVisibility();
        }
    }

    removeCardGroup(cardInGroup) {
        const container = cardInGroup.closest('#main-cards-container, #additional-cards-container');
        const cardTitle = cardInGroup.getAttribute('data-card-title');
        if (!container || !cardTitle) {
            console.warn('[removeCardGroup] Could not find container or title. Removing only the provided card.');
            cardInGroup.remove();
            this.updateSubtotal();
            return;
        }

        const cardsInGroup = Array.from(container.querySelectorAll(`.product-card[data-card-title="${cardTitle}"]`));
        cardsInGroup.forEach(groupCard => {
            // *** FIX: Remove the associated modal before removing the card ***
            const modalId = groupCard.dataset.modalId;
            if (modalId) {
                const modalElement = document.getElementById(modalId);
                if (modalElement) {
                    modalElement.remove();
                }
            }
            groupCard.remove();
        });
        this.setupFieldVisibility();
        this.updateSubtotal();
    }

    showErrorOnCard(card, message) {
        card.classList.add('error-state');

        let errorBadge = card.querySelector('.error-badge');

        if (!errorBadge) {
            errorBadge = document.createElement('div');
            errorBadge.className = 'error-badge absolute top-0 right-0 bg-red-500 text-white text-xs px-2 py-1 rounded-bl-md z-10';
            card.appendChild(errorBadge);
        }

        errorBadge.textContent = message;

        setTimeout(() => {
            errorBadge.remove();
            card.classList.remove('error-state');
        }, 5000);
    }

    updateFieldVisibility(modal, fieldName, visible) {
        const desktopContainer = modal.querySelector(`.md\\:flex .option-group[data-field-group="${fieldName}"]`);
        const mobileContainer = modal.querySelector(`.mobile-modal-content .option-group[data-field-group="${fieldName}"]`);

        if (desktopContainer) {
            if (!visible) {
                desktopContainer.classList.add('propagated-field-hidden');
                desktopContainer.setAttribute('data-propagated-field', 'true');
                desktopContainer.style.display = 'none';
            } else {
                desktopContainer.classList.remove('propagated-field-hidden');
                desktopContainer.removeAttribute('data-propagated-field');
                desktopContainer.style.display = '';
            }
        }

        if (mobileContainer) {
            if (!visible) {
                mobileContainer.classList.add('propagated-field-hidden');
                mobileContainer.setAttribute('data-propagated-field', 'true');
                mobileContainer.style.display = 'none';
            } else {
                mobileContainer.classList.remove('propagated-field-hidden');
                mobileContainer.removeAttribute('data-propagated-field');
                mobileContainer.style.display = '';
            }
        }
    }

    /**
     * Manages the loading state for a single, dynamically added card's image.
     * @param {HTMLElement} card - The card element whose image needs monitoring.
     */
    setupImageLoadingForCard(card) {
        if (!card) return;

        // CORRECTED: Find ALL image containers within the specific card.
        const imageContainers = card.querySelectorAll('.image-container');

        // If no containers are found, there's nothing to do.
        if (imageContainers.length === 0) {
            return;
        }

        // Iterate over each container (e.g., one for mobile, one for desktop).
        imageContainers.forEach(container => {
            const imgElement = container.querySelector('img');

            // If the necessary elements aren't found or it's not in a loading state, skip to the next one.
            if (!imgElement || !container.classList.contains('image-loading')) {
                return; // Acts as 'continue' in a forEach loop.
            }

            // A reusable function to remove the loading class and clean up event listeners.
            const finishLoading = () => {
                container.classList.remove('image-loading');
                imgElement.onload = null; // Clean up listeners to prevent memory leaks.
                imgElement.onerror = null;
            };

            // A function to handle loading errors.
            const handleError = () => {
                console.error('Failed to load image for new card:', imgElement.src);
                container.classList.add('image-error');
                finishLoading(); // Still finish the loading process, but with an error state.
            };

            // If the image is already loaded (e.g., from browser cache), finish immediately.
            if (imgElement.complete) {
                finishLoading();
            } else {
                // Otherwise, set listeners to finish when the image loads or errors out.
                imgElement.onload = finishLoading;
                imgElement.onerror = handleError;
            }
        });
    }
}
